import {
  IdentificationSchema,
  PlayerCrossStatisticsSchema,
  StatisticsSchema,
} from './schemas';
import type { Statistics, PlayerSlot } from '@/types/statistics';
import type { PlayerCrossStatistics } from '@/types/playerCrossStats';
import type { IdentificationSegment } from './identifyPlayers';

// Env vars are read at call time (via these accessors), not captured at module
// load. Vite still statically inlines each `import.meta.env.X` literal at build,
// so production behavior is identical — but reading lazily lets tests override
// them with `vi.stubEnv()` (a module-scope capture would win over the stub).
const apiBaseUrl = () => import.meta.env.VITE_API_BASE_URL;
const localJson = () => import.meta.env.VITE_LOCAL_JSON;
const localPlayerJson = () => import.meta.env.VITE_LOCAL_PLAYER_JSON;

/** Who is viewing the report: their player id, and their report token when they came signed in. */
export interface ReportViewer {
  playerId: string;
  reportToken?: string | null;
}

const bearer = (reportToken?: string | null): Record<string, string> =>
  reportToken ? { Authorization: `Bearer ${reportToken}` } : {};

const playerQuery = (viewer: ReportViewer) =>
  `?player_id=${encodeURIComponent(viewer.playerId)}`;

/**
 * The error message when the match has no report: the API answers 409 for a
 * match without statistics (recorded only, or its analysis not ready yet).
 */
export const NO_REPORT = 'NO_REPORT';

async function fetchAndValidateStatistics(
  url: string,
  headers: Record<string, string> = {},
): Promise<Statistics> {
  const res = await fetch(url, { headers });
  if (res.status === 409) {
    throw new Error(NO_REPORT);
  }
  if (!res.ok) {
    throw new Error(`Failed to fetch statistics: ${res.status}`);
  }

  const json = await res.json();
  const parsed = StatisticsSchema.safeParse(json);

  if (!parsed.success) {
    console.error('Statistics validation errors:', parsed.error.issues);
    throw new Error('Invalid statistics data');
  }

  return parsed.data;
}

/**
 * Fetch and validate statistics data from the backend.
 * When VITE_LOCAL_JSON is set, loads from that public-folder path instead.
 *
 * With a `viewer`, the backend serves an owner who corrected who is who their
 * own report, and says what they answered (`owner_report`).
 */
export async function fetchStatistics(
  sessionId: string,
  viewer?: ReportViewer,
): Promise<Statistics> {
  const LOCAL_JSON = localJson();
  const API_BASE_URL = apiBaseUrl();
  if (LOCAL_JSON) {
    return fetchAndValidateStatistics(
      LOCAL_JSON.startsWith('/') ? LOCAL_JSON : `/${LOCAL_JSON}`,
    );
  }
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured');
  }
  const query = viewer ? playerQuery(viewer) : '';
  return fetchAndValidateStatistics(
    `${API_BASE_URL}/sessions/${sessionId}/statistics${query}`,
    bearer(viewer?.reportToken),
  );
}

async function identificationRequest(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  reportToken?: string | null,
  body?: unknown,
): Promise<Response> {
  const API_BASE_URL = apiBaseUrl();
  if (!API_BASE_URL) {
    throw new Error('VITE_API_BASE_URL is not configured');
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...bearer(reportToken),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new Error(`Identification request failed: ${res.status}`);
  }
  return res;
}

/** The session's blocks for an owner to tell who is who in. */
export async function fetchIdentification(
  sessionId: string,
  viewer: ReportViewer,
): Promise<IdentificationSegment[]> {
  const res = await identificationRequest(
    'GET',
    `/statistics/${sessionId}/identification${playerQuery(viewer)}`,
    viewer.reportToken,
  );
  const parsed = IdentificationSchema.safeParse(await res.json());
  if (!parsed.success) {
    console.error('Identification validation errors:', parsed.error.issues);
    throw new Error('Invalid identification data');
  }
  return parsed.data.segments;
}

/** Save the owner's who-is-who: their report becomes the statistics corrected with it. */
export async function saveCorrection(
  sessionId: string,
  viewer: ReportViewer,
  playersMapping: (Record<number, number> | null)[],
): Promise<void> {
  await identificationRequest(
    'PUT',
    `/statistics/${sessionId}/identification`,
    viewer.reportToken,
    { player_id: viewer.playerId, players_mapping: playersMapping },
  );
}

/**
 * The report corrected with a who-is-who, saving nothing: for a viewer who
 * doesn't own the match. Same shape as fetchStatistics, with no one's
 * identification in it.
 */
export async function previewCorrection(
  sessionId: string,
  playersMapping: (Record<number, number> | null)[],
): Promise<Statistics> {
  const res = await identificationRequest(
    'POST',
    `/statistics/${sessionId}/identification/preview`,
    null,
    { players_mapping: playersMapping },
  );
  const parsed = StatisticsSchema.safeParse(await res.json());
  if (!parsed.success) {
    console.error('Preview validation errors:', parsed.error.issues);
    throw new Error('Invalid statistics data');
  }
  return parsed.data;
}

/** Save who the owner is in their corrected report (a reference player). */
export async function identifyOwner(
  sessionId: string,
  viewer: ReportViewer,
  identifiedAs: string,
): Promise<void> {
  await identificationRequest(
    'PUT',
    `/statistics/${sessionId}/identification/me`,
    viewer.reportToken,
    { player_id: viewer.playerId, identified_as: identifiedAs },
  );
}

/** Start over: delete the owner's correction and identification. */
export async function resetIdentification(
  sessionId: string,
  viewer: ReportViewer,
): Promise<void> {
  await identificationRequest(
    'DELETE',
    `/statistics/${sessionId}/identification${playerQuery(viewer)}`,
    viewer.reportToken,
  );
}

/**
 * Fetch and validate statistics from an arbitrary fully-qualified URL.
 * Used by tournament mode where the JSON URL (including SAS token) is
 * passed via query parameter.
 *
 * Tournament JSONs are produced by a different pipeline and don't include
 * `metadata.block_id` on each segment (the regular flow needs it for
 * correspondence persistence; tournament mode does not). We inject a
 * deterministic synthetic id so the existing schema stays strict.
 */
export async function fetchStatisticsFromUrl(url: string): Promise<Statistics> {
  // Strip any whitespace that may have been injected during URL paste
  // (some chat clients wrap long URLs with soft line-breaks that get
  // copied as literal spaces, corrupting the SAS signature and path).
  const cleanUrl = url.replace(/\s+/g, '');
  const res = await fetch(cleanUrl);
  if (!res.ok) {
    throw new Error(`Failed to fetch statistics: ${res.status}`);
  }

  const json = (await res.json()) as unknown;
  if (json && typeof json === 'object') {
    const root = json as { segments_statistics?: unknown[] };
    if (Array.isArray(root.segments_statistics)) {
      root.segments_statistics.forEach((seg, i) => {
        if (seg && typeof seg === 'object') {
          const segObj = seg as { metadata?: { block_id?: string } };
          segObj.metadata = segObj.metadata ?? {};
          if (!segObj.metadata.block_id) {
            segObj.metadata.block_id = `tournament-${i}`;
          }
        }
      });
    }
  }

  const parsed = StatisticsSchema.safeParse(json);
  if (!parsed.success) {
    console.error('Statistics validation errors:', parsed.error.issues);
    throw new Error('Invalid statistics data');
  }
  return parsed.data;
}

/**
 * Send player identification results back to the backend.
 * `associations` is a {block_id: slot} map; the backend upserts per block,
 * so omitted block_ids are left untouched.
 *
 * `reportToken` (from the Player Dashboard, see reportTokenFromHash) lets a
 * signed-in owner save under their account id; without it the backend only
 * accepts players registered on the session.
 */
export async function putUsernameCorrespondence(
  sessionId: string,
  payload: { player_id: string; associations: Record<string, PlayerSlot> },
  reportToken?: string | null,
): Promise<void> {
  const API_BASE_URL = apiBaseUrl();
  if (!API_BASE_URL) return;

  const res = await fetch(
    `${API_BASE_URL}/sessions/${sessionId}/username-correspondence`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(reportToken ? { Authorization: `Bearer ${reportToken}` } : {}),
      },
      body: JSON.stringify(payload),
    },
  );

  if (!res.ok) {
    throw new Error(`Failed to put correspondence: ${res.status}`);
  }
}

/**
 * Check whether a player is eligible for the Elite program.
 */
export async function checkEliteEligibility(
  playerId: string,
): Promise<boolean> {
  const API_BASE_URL = apiBaseUrl();
  if (!API_BASE_URL) return false;

  try {
    const res = await fetch(`${API_BASE_URL}/players/${playerId}/elite`);
    if (!res.ok) return false;
    return (await res.json()) === true;
  } catch {
    return false;
  }
}

/**
 * Dismiss the Elite program banner for a player (fire-and-forget).
 */
export function dismissEliteBanner(playerId: string): void {
  const API_BASE_URL = apiBaseUrl();
  if (!API_BASE_URL) return;

  fetch(`${API_BASE_URL}/players/${playerId}/dismiss`, {
    method: 'PATCH',
  }).catch(() => {});
}

/**
 * Fetch and validate the cross-session statistics payload for a single player.
 *
 * Honors `VITE_LOCAL_PLAYER_JSON` (preferred for cross-stats fixture) and
 * falls back to `VITE_LOCAL_JSON` so existing local-dev workflows keep working
 * when only one fixture is configured.
 */
export async function fetchPlayerCrossStats(
  playerId: string,
  params?: { startDate?: string; endDate?: string },
): Promise<PlayerCrossStatistics> {
  const LOCAL_PLAYER_JSON = localPlayerJson();
  const LOCAL_JSON = localJson();
  const API_BASE_URL = apiBaseUrl();
  const localFixture = LOCAL_PLAYER_JSON || LOCAL_JSON;

  const url = localFixture
    ? localFixture.startsWith('/')
      ? localFixture
      : `/${localFixture}`
    : (() => {
        if (!API_BASE_URL) {
          throw new Error('VITE_API_BASE_URL is not configured');
        }
        const qs = new URLSearchParams();
        if (params?.startDate) qs.set('start_date', params.startDate);
        if (params?.endDate) qs.set('end_date', params.endDate);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const safePlayerId = encodeURIComponent(playerId);
        return `${API_BASE_URL}/players/${safePlayerId}/cross-statistics${suffix}`;
      })();

  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch player cross-statistics: ${res.status}`);
  }

  const json = await res.json();
  const parsed = PlayerCrossStatisticsSchema.safeParse(json);

  if (!parsed.success) {
    console.error(
      'Player cross-statistics validation errors:',
      parsed.error.issues,
    );
    throw new Error('Invalid player cross-statistics data');
  }

  return parsed.data;
}
